import formulaEngine from './formula-eval'
import { isSectionNASelected } from '../tuft-lock-na-utility'
import {
    ChangeAction,
    FORMULA_RESPONSE_ID,
    TemplateResponseType,
    isFormulaQuestion,
    isNumberInputQuestion,
    isTextInputQuestion,
} from '../constant/template'

const engine = formulaEngine.evaluateFormula
    ? formulaEngine
    : formulaEngine.default

const {
    evaluateFormula,
    formatFormulaDisplay,
} = engine

export { evaluateFormula, formatFormulaDisplay }

/** Attach the unit once. Server preview/display is the plain number. */
export const ensureFormulaSuffix = (text, suffix) => {
    const value = String(text || '').trim()
    if (!value || value === '—') return ''
    const unit = String(suffix || '').trim().slice(0, 20)
    if (!unit) return value
    if (unit === '%') {
        return value.endsWith('%') ? value : `${value}%`
    }
    if (value.endsWith(unit)) return value
    return `${value} ${unit}`
}

const GUID_RX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const isQuestionGuid = value => GUID_RX.test(String(value || '').trim())

export const createQuestionGuid = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }
    const hex = () => Math.floor(Math.random() * 16).toString(16)
    const block = length => Array.from({ length }, hex).join('')
    return `${block(8)}-${block(4)}-4${block(3)}-a${block(3)}-${block(12)}`
}

export const readQuestionGuid = question => {
    const candidates = [question?.QuestionId, question?.QuestionUId]
    for (let i = 0; i < candidates.length; i += 1) {
        if (isQuestionGuid(candidates[i])) return String(candidates[i])
    }
    return ''
}

export const plainQuestionText = value =>
    String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()

const sameGuid = (left, right) =>
    String(left || '').trim().toLowerCase() ===
    String(right || '').trim().toLowerCase()

const templateGuidOf = question => {
    if (isQuestionGuid(question?.QuestionOriginalId)) {
        return String(question.QuestionOriginalId)
    }
    if (isQuestionGuid(question?.QuestionId)) return String(question.QuestionId)
    return ''
}

export const isDeletedTemplateQuestion = question =>
    Number(question?.ChangeAction) === ChangeAction.Delete

/**
 * Number every question the way the builder will send it, and assign a stable
 * GUID to any row that does not have one yet.
 */
export const buildFormulaCatalog = ({
    sections = [],
    currentSectionId,
    currentQuestions,
    sectionName = '',
    selfLocalId,
    selfGuid,
    selfLabel = '',
    guidCache,
}) => {
    const cache = guidCache || new Map()
    const visibleSections = (Array.isArray(sections) ? sections : []).filter(
        section => Number(section?.ChangeAction) !== ChangeAction.Delete,
    )
    const currentKey = currentSectionId == null ? '' : String(currentSectionId)
    const hasCurrent = visibleSections.some(
        section => String(section?.SectionId) === currentKey,
    )
    const ordered = hasCurrent
        ? visibleSections
        : [
            ...visibleSections,
            {
                SectionId: currentSectionId,
                SectionName: sectionName,
                Questions: currentQuestions || [],
            },
        ]

    const guidPatches = []
    const rows = []
    let number = 0

    ordered.forEach(section => {
        const isCurrent = String(section?.SectionId) === currentKey
        let list = isCurrent ? currentQuestions || [] : section?.Questions || []
        list = (Array.isArray(list) ? list : []).filter(
            question => !isDeletedTemplateQuestion(question),
        )
        const selfAlreadyListed = list.some(
            question =>
                (selfLocalId != null &&
                    String(question?.Id) === String(selfLocalId)) ||
                (selfGuid && sameGuid(readQuestionGuid(question), selfGuid)),
        )
        if (isCurrent && selfGuid && !selfAlreadyListed) {
            list = [
                ...list,
                {
                    Id: selfLocalId || selfGuid,
                    QuestionId: selfGuid,
                    Question: selfLabel,
                    QuestionType: TemplateResponseType.Single,
                    QuestionResponseId: FORMULA_RESPONSE_ID,
                    __formulaDraft: true,
                },
            ]
        }

        list.forEach(question => {
            number += 1
            const isSelf =
                (selfLocalId != null &&
                    String(question?.Id) === String(selfLocalId)) ||
                (selfGuid && sameGuid(readQuestionGuid(question), selfGuid)) ||
                !!question.__formulaDraft
            let guid = isSelf ? selfGuid : readQuestionGuid(question)
            if (!guid) {
                const cacheKey = String(question?.Id ?? `n-${number}`)
                guid = cache.get(cacheKey) || createQuestionGuid()
                cache.set(cacheKey, guid)
                if (!question.__formulaDraft && question?.Id != null) {
                    guidPatches.push({
                        Id: question.Id,
                        QuestionId: guid,
                        SectionId: section?.SectionId,
                    })
                }
            }
            const label =
                plainQuestionText(question?.Question || question?.FAQ) ||
                (isSelf ? plainQuestionText(selfLabel) : '') ||
                `Question ${number}`
            rows.push({
                QuestionId: guid,
                Number: number,
                Label: label,
                QuestionType: Number(
                    question?.QuestionType ?? question?.TypeOfResponse ?? TemplateResponseType.Single,
                ),
                QuestionResponseId: Number(question?.QuestionResponseId) || 0,
                localId: question?.Id,
                sectionId: section?.SectionId,
                sectionName: section?.SectionName || '',
                isSelf: !!isSelf,
                isInput:
                    !isSelf &&
                    (isNumberInputQuestion(question) ||
                        isTextInputQuestion(question) ||
                        isFormulaQuestion(question) ||
                        (Number(question?.QuestionResponseId) === FORMULA_RESPONSE_ID &&
                            Number(question?.QuestionType ?? question?.TypeOfResponse) ===
                                TemplateResponseType.Single)),
            })
        })
    })

    const sectionCount = new Set(
        rows.filter(row => row.isInput).map(row => String(row.sectionName || row.sectionId)),
    ).size

    const options = rows
        .filter(row => row.isInput && row.QuestionId)
        .map(row => ({
            key: row.QuestionId,
            value: row.QuestionId,
            text:
                sectionCount > 1 && row.sectionName
                    ? `${row.sectionName} · [${row.Number}] ${row.Label}`
                    : `[${row.Number}] ${row.Label}`,
            number: row.Number,
            label: row.Label,
        }))

    return {
        questions: rows.map(row => ({
            QuestionId: row.QuestionId,
            Number: row.Number,
            Label: row.Label,
            QuestionType: row.QuestionType,
            QuestionResponseId: row.QuestionResponseId,
        })),
        options,
        guidPatches,
    }
}

export const applyQuestionGuidPatches = (questions, patches) => {
    if (!Array.isArray(questions) || !patches?.length) return questions
    let changed = false
    const next = questions.map(question => {
        const patch = patches.find(item => String(item.Id) === String(question?.Id))
        if (!patch || readQuestionGuid(question)) return question
        changed = true
        const updated = { ...question, QuestionId: patch.QuestionId }
        const action = Number(question?.ChangeAction)
        const persisted =
            question?.Id != null &&
            question?.Id !== '' &&
            !String(question.Id).includes('.')
        if (
            persisted &&
            action !== ChangeAction.New &&
            action !== ChangeAction.Delete &&
            action !== ChangeAction.Update
        ) {
            updated.ChangeAction = ChangeAction.Update
        }
        return updated
    })
    return changed ? next : questions
}

const readBody = error => {
    if (!error) return {}
    if (error.error && typeof error.error === 'object') return error.error
    if (error.response?.data && typeof error.response.data === 'object') {
        return error.response.data
    }
    return typeof error === 'object' ? error : {}
}

const FORMULA_SAVE_MESSAGES = {
    FORMULA_INVALID: 'One or more formula questions are not valid.',
    FORMULA_INPUT_IN_USE:
        'Fix the formula first. A question you changed is still used by a formula.',
    FORMULA_NOT_ENABLED: 'Formula questions are not enabled for this company.',
    FORMULA_NOT_INSTALLED: 'Formula questions are not available on this server yet.',
    FORMULA_CHECK_FAILED: 'The formula could not be checked. Please try again.',
}

export const formatFormulaSaveError = error => {
    const body = readBody(error)
    const code = field(body, 'ErrorCode', 'errorCode') || field(body, 'Code', 'code') || ''
    if (!String(code).startsWith('FORMULA_')) return ''
    const parts = [
        field(body, 'Message', 'message') ||
            FORMULA_SAVE_MESSAGES[code] ||
            'The formula could not be saved.',
    ]
    let details = []
    if (Array.isArray(body.Errors)) details = body.Errors
    else if (Array.isArray(body.errors)) details = body.errors
    details.slice(0, 4).forEach(item => {
        const name = field(item, 'Question', 'question')
            ? `"${plainQuestionText(field(item, 'Question', 'question'))}"`
            : ''
        const message = field(item, 'Message', 'message') || field(item, 'Code', 'code') || ''
        if (message) parts.push([name, message].filter(Boolean).join(': '))
    })
    if (details.length > 4) parts.push(`${details.length - 4} more`)
    return parts.join(' — ')
}

const field = (obj, pascal, camel) => {
    if (!obj || typeof obj !== 'object') return undefined
    if (obj[pascal] != null) return obj[pascal]
    return obj[camel]
}

const unwrapFormulaBody = raw => {
    if (!raw || typeof raw !== 'object') return {}
    const looksLikeCheck =
        raw.Valid != null ||
        raw.valid != null ||
        raw.Expr ||
        raw.expr ||
        raw.Errors ||
        raw.errors ||
        raw.Cells ||
        raw.cells
    if (looksLikeCheck) return raw
    return raw.Result || raw.result || raw.Data || raw.data || raw
}

export const readFormulaCheck = raw => {
    const body = unwrapFormulaBody(raw)
    const errorList = field(body, 'Errors', 'errors')
    const errors = (Array.isArray(errorList) ? errorList : []).map(item => ({
        Code: field(item, 'Code', 'code') || '',
        Message:
            field(item, 'Message', 'message') ||
            field(item, 'Code', 'code') ||
            'The formula is not valid.',
        Position: field(item, 'Position', 'position'),
    }))
    const cellList = field(body, 'Cells', 'cells')
    const cells = (Array.isArray(cellList) ? cellList : []).map(item => ({
        Cell: field(item, 'Cell', 'cell') || '',
        QuestionId: field(item, 'QuestionId', 'questionId') || '',
        Label: field(item, 'Label', 'label') || '',
    }))
    const inputList = field(body, 'Inputs', 'inputs')
    const inputs = (Array.isArray(inputList) ? inputList : []).map(item => ({
        QuestionId: field(item, 'QuestionId', 'questionId') || '',
        Number: field(item, 'Number', 'number'),
        Label: field(item, 'Label', 'label') || '',
    }))
    const preview = field(body, 'Preview', 'preview') || null
    return {
        valid: field(body, 'Valid', 'valid') === true,
        expr: field(body, 'Expr', 'expr') || '',
        displayText: field(body, 'DisplayText', 'displayText') || '',
        errors,
        cells,
        inputs,
        preview,
    }
}

/** Formulas[] from Inspection save / answer, including a Result wrapper. */
export const readServerFormulaList = response => {
    if (!response) return []
    if (Array.isArray(response)) return response
    const body = unwrapFormulaBody(response)
    const list = field(body, 'Formulas', 'formulas')
    if (Array.isArray(list)) return list
    const nested = field(response, 'Formulas', 'formulas')
    return Array.isArray(nested) ? nested : []
}

const formulaQuestionKey = question => String(question?.Id ?? '')

const flattenFormulaQuestions = sections => {
    const rows = []
    const sectionList = sections || []
    sectionList.forEach(section => {
        const sectionNA = isSectionNASelected(section)
        const sectionCopied = !!(section?.IsCopiedSection || section?.IsCopied)
        const questions = section?.Questions || []
        questions.forEach(question => {
            rows.push({
                question,
                sectionId: question?.SectionId ?? section?.SectionId,
                isCopied: !!(question?.IsCopiedSection || sectionCopied),
                sectionNA,
            })
        })
    })
    return rows
}

const needsInputMessage = (blankInputs, rows) => {
    const labels = []
    const missing = blankInputs || []
    missing.forEach(guid => {
        const match = rows.find(row => sameGuid(templateGuidOf(row.question), guid))
        const label = plainQuestionText(
            match?.question?.FAQ || match?.question?.Question || '',
        )
        if (label && !labels.includes(label)) labels.push(label)
    })
    if (!labels.length) return ''
    return `Needs: ${labels.join(', ')}`
}

/**
 * Recalculate every formula in the audit from the current number answers.
 * Formulas that read other formulas are resolved by repeating until stable.
 */
export const applyLiveFormulas = sections => {
    if (!Array.isArray(sections)) return sections
    const rows = flattenFormulaQuestions(sections)
    if (!rows.some(row => isFormulaQuestion(row.question) && row.question?.Formula?.Expr)) {
        return sections
    }

    const texts = new Map()
    rows.forEach(row => {
        texts.set(
            formulaQuestionKey(row.question),
            row.question?.Answers?.SingleResponse?.Response ?? '',
        )
    })

    const results = new Map()
    let guard = 0
    const calculatePass = () => {
        let passChanged = false
        rows.forEach(row => {
            const formula = row.question?.Formula
            if (!isFormulaQuestion(row.question) || !formula?.Expr) return
            const lookup = guid => {
                const same = rows.find(
                    item =>
                        sameGuid(templateGuidOf(item.question), guid) &&
                        String(item.sectionId) === String(row.sectionId),
                )
                const original = rows.find(
                    item =>
                        sameGuid(templateGuidOf(item.question), guid) &&
                        !item.isCopied,
                )
                const any = rows.find(item =>
                    sameGuid(templateGuidOf(item.question), guid),
                )
                const found = same || original || any
                if (!found || found.sectionNA) return null
                return texts.get(formulaQuestionKey(found.question)) ?? null
            }
            const evaluated = evaluateFormula(
                formula.Expr,
                lookup,
                formula.Decimals,
            )
            let text = ''
            let display = ''
            let message = null
            if (evaluated.errorCode === 'BLANK_INPUT') {
                message =
                    needsInputMessage(evaluated.blankInputs, rows) ||
                    evaluated.message ||
                    'Waiting for input values.'
            } else if (evaluated.errorCode) {
                message = evaluated.message || 'The formula could not be calculated.'
            } else {
                text = evaluated.text || ''
                display = formatFormulaDisplay(text, formula.Suffix)
            }
            const key = formulaQuestionKey(row.question)
            if ((texts.get(key) ?? '') !== text) {
                texts.set(key, text)
                passChanged = true
            }
            results.set(key, { text, display, message })
        })
        return passChanged
    }
    while (guard < 12 && calculatePass()) {
        guard += 1
    }

    let anyWritten = false
    const nextSections = sections.map(section => {
        let sectionChanged = false
        const questions = (section.Questions || []).map(question => {
            const result = results.get(formulaQuestionKey(question))
            if (!result) return question
            const previousText = question?.Answers?.SingleResponse?.Response ?? ''
            const previousDisplay = question?.FormulaDisplay || ''
            const previousMessage = question?.FormulaMessage || null
            if (
                previousText === result.text &&
                previousDisplay === result.display &&
                previousMessage === result.message
            ) {
                return question
            }
            sectionChanged = true
            anyWritten = true
            return {
                ...question,
                Score: 0,
                FormulaDisplay: result.display,
                FormulaMessage: result.message,
                Answers: {
                    ...(question.Answers || {}),
                    SingleResponse: {
                        ...(question.Answers?.SingleResponse || {}),
                        Response: result.text,
                    },
                },
            }
        })
        return sectionChanged ? { ...section, Questions: questions } : section
    })
    return anyWritten ? nextSections : sections
}

/** The server ignores a client value for a formula question. */
export const omitFormulaAnswerValues = audit => {
    if (!audit?.Sections) return audit
    let changed = false
    const sections = audit.Sections.map(section => {
        let sectionChanged = false
        const questions = (section.Questions || []).map(question => {
            if (!isFormulaQuestion(question)) return question
            const response = question?.Answers?.SingleResponse
            if (!response || response.Response === '' || response.Response == null) {
                return question
            }
            sectionChanged = true
            changed = true
            return {
                ...question,
                Answers: {
                    ...question.Answers,
                    SingleResponse: {
                        ...response,
                        Response: '',
                    },
                },
            }
        })
        return sectionChanged ? { ...section, Questions: questions } : section
    })
    return changed ? { ...audit, Sections: sections } : audit
}

export const applyServerFormulaResults = (audit, formulas) => {
    if (!audit?.Sections || !Array.isArray(formulas) || !formulas.length) return audit
    const byAuditId = new Map()
    const byGuid = new Map()
    formulas.forEach(item => {
        const auditQuestionId = field(item, 'AuditQuestionId', 'auditQuestionId')
        const questionId = field(item, 'QuestionId', 'questionId')
        if (auditQuestionId != null) {
            byAuditId.set(String(auditQuestionId), item)
        }
        if (questionId) byGuid.set(String(questionId).toLowerCase(), item)
    })
    const guidCounts = new Map()
    audit.Sections.forEach(section => {
        const questions = section.Questions || []
        questions.forEach(question => {
            const guid = templateGuidOf(question).toLowerCase()
            if (!guid) return
            guidCounts.set(guid, (guidCounts.get(guid) || 0) + 1)
        })
    })
    let changed = false
    const sections = audit.Sections.map(section => {
        let sectionChanged = false
        const questions = (section.Questions || []).map(question => {
            const guid = templateGuidOf(question).toLowerCase()
            const hit =
                byAuditId.get(String(question?.Id)) ||
                (guid && guidCounts.get(guid) === 1 ? byGuid.get(guid) : null)
            if (!hit) return question
            sectionChanged = true
            changed = true
            const valueRaw = field(hit, 'Value', 'value')
            const value = valueRaw == null ? '' : String(valueRaw)
            return {
                ...question,
                FormulaDisplay: field(hit, 'Display', 'display') || '',
                FormulaMessage: field(hit, 'Message', 'message') || null,
                Answers: {
                    ...(question.Answers || {}),
                    SingleResponse: {
                        ...(question.Answers?.SingleResponse || {}),
                        Response: value,
                    },
                },
            }
        })
        return sectionChanged ? { ...section, Questions: questions } : section
    })
    return changed ? { ...audit, Sections: sections } : audit
}
